/**
 * ProfileCoursesCard
 * Surfaces the user's course progress on the Profile page. Adapts to the
 * active space:
 *   • reader space → /api/reader/courses/catalog → "/reading-course"
 *   • writer space → /api/writer/courses/catalog → "/writer-courses"
 *
 * Renders a progress bar, the next-up module, and a tap-through CTA.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, ArrowRight, CheckCircle2, Lock, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VARIANTS = {
  reader: {
    title: 'Reading Courses',
    endpoint: '/api/reader/courses/catalog',
    route: '/reading-course',
    accent: 'from-indigo-500 to-cyan-500',
    accentText: 'text-indigo-300',
    ring: 'border-indigo-500/40',
    badge: 'bg-indigo-500/20 text-indigo-200 border-indigo-400/50',
  },
  writer: {
    title: 'Scribe Courses',
    endpoint: '/api/writer/courses/catalog',
    route: '/writer-courses',
    accent: 'from-emerald-500 to-amber-500',
    accentText: 'text-emerald-300',
    ring: 'border-emerald-500/40',
    badge: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/50',
  },
};

const ProfileCoursesCard = ({ variant = 'reader' }) => {
  const navigate = useNavigate();
  const v = VARIANTS[variant] || VARIANTS.reader;
  const [modules, setModules] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}${v.endpoint}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!cancelled) setModules(d.modules || []);
      } catch {
        if (!cancelled) setModules([]);
      }
    })();
    return () => { cancelled = true; };
  }, [v.endpoint]);

  if (modules === null) {
    return (
      <Card className="bg-slate-800/50 border-slate-700 md:col-span-2" data-testid={`profile-courses-${variant}-loading`}>
        <CardContent className="p-6 flex items-center gap-3 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading {v.title.toLowerCase()}…
        </CardContent>
      </Card>
    );
  }

  const total = modules.length;
  const completed = modules.filter((m) => m.completed).length;
  const pct = total ? Math.round((completed / total) * 100) : 0;
  // Next-up = first non-completed, non-locked module.
  const nextUp = modules.find((m) => !m.completed && !m.locked) || null;

  return (
    <Card
      className={`bg-slate-800/50 border ${v.ring} md:col-span-2`}
      data-testid={`profile-courses-${variant}`}
    >
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className={`text-white inline-flex items-center gap-2 ${v.accentText}`}>
          <GraduationCap className="w-5 h-5" />
          <span className="text-white">{v.title}</span>
        </CardTitle>
        <span className={`text-[11px] px-2 py-1 rounded-full border ${v.badge} font-bold`}>
          {completed}/{total} done
        </span>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress bar */}
        <div>
          <div className="flex items-center justify-between text-xs text-slate-300 mb-1.5">
            <span>Overall progress</span>
            <span className="font-bold tabular-nums">{pct}%</span>
          </div>
          <div className="w-full h-2 rounded-full bg-slate-700/70 overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r ${v.accent} transition-all duration-700`}
              style={{ width: `${pct}%` }}
              data-testid={`profile-courses-${variant}-progress`}
            />
          </div>
        </div>

        {/* Next-up */}
        {nextUp ? (
          <button
            type="button"
            onClick={() => navigate(`${v.route}/lesson/${nextUp.id}`)}
            className="w-full text-left flex items-center gap-3 p-3 rounded-xl bg-slate-900/60 border border-slate-700 hover:border-slate-500 hover:bg-slate-900 transition-colors"
            data-testid={`profile-courses-${variant}-next`}
          >
            <div className={`inline-flex w-9 h-9 items-center justify-center rounded-lg bg-gradient-to-br ${v.accent} text-white font-black shrink-0`}>
              {nextUp.order || '·'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Next up</p>
              <p className="text-sm text-white font-semibold truncate">{nextUp.title}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">
                +{nextUp.xp} XP · +{nextUp.coins} coins
              </p>
            </div>
            <ArrowRight className={`w-4 h-4 ${v.accentText} shrink-0`} />
          </button>
        ) : completed === total && total > 0 ? (
          <div className="flex items-center gap-2 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3" data-testid={`profile-courses-${variant}-done`}>
            <CheckCircle2 className="w-4 h-4" />
            <span>Every {variant === 'reader' ? 'reader' : 'writer'} module completed. Nice.</span>
          </div>
        ) : (
          <p className="text-sm text-slate-500">No modules available yet.</p>
        )}

        {/* Module ribbon */}
        {total > 0 && (
          <div
            className="flex items-center gap-1.5 overflow-x-auto -mx-1 px-1 scrollbar-hide"
            data-testid={`profile-courses-${variant}-ribbon`}
          >
            {modules.map((m) => (
              <button
                key={m.id}
                type="button"
                disabled={m.locked}
                onClick={() => !m.locked && navigate(`${v.route}/lesson/${m.id}`)}
                title={m.title}
                className={`relative shrink-0 w-9 h-9 rounded-lg border text-[11px] font-black flex items-center justify-center transition-all ${
                  m.completed
                    ? `bg-gradient-to-br ${v.accent} border-transparent text-white shadow-md`
                    : m.locked
                    ? 'bg-slate-900/40 border-slate-800 text-slate-600 cursor-not-allowed'
                    : 'bg-slate-900/60 border-slate-700 text-slate-300 hover:border-slate-500'
                }`}
                data-testid={`profile-courses-${variant}-tile-${m.id}`}
              >
                {m.completed ? (
                  <CheckCircle2 className="w-3.5 h-3.5" />
                ) : m.locked ? (
                  <Lock className="w-3.5 h-3.5" />
                ) : (
                  m.order
                )}
              </button>
            ))}
          </div>
        )}

        <Button
          onClick={() => navigate(v.route)}
          variant="outline"
          className="w-full border-slate-600 text-white hover:bg-slate-700"
          data-testid={`profile-courses-${variant}-open`}
        >
          Open {v.title}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default ProfileCoursesCard;
